/** 担当者選択コンポーネント */
<template>
  <div class="schedule-assign-user-selector">
    <div class="select-container">
      <b-form-select
        v-bind:id="elementId.value"
        multiple
        class="input-multi-select"
        v-bind:select-size="5"
        value-field="user_id"
        text-field="user_name"
        v-model="dummyValue"
        v-bind:options="currentUsers"
        v-bind:state="state"
      />
    </div>
    <div class="user-action-wrapper">
      <b-button
        size="sm"
        variant="success"
        v-bind:id="elementId.button"
        v-on:click="showModal"
        v-t="'appScheduleAssignUserSelector.action.changeAssignees'"
      />
      <span
        v-if="showAssignMeLink"
        class="assign-me-link"
        v-bind:id="elementId.link"
        v-t="'appScheduleAssignUserSelector.action.assignMe'"
        v-on:click="$_onClickAssignMe"
      />
    </div>
    <!-- 担当者選択モーダル -->
    <ifield-b-modal
      v-bind:id="elementId.modal"
      ref="modal"
      size="md"
      content-class="schedule-assign-user-selector-content"
      body-class="schedule-assign-user-selector-body"
      ok-variant="success"
      centered
      scrollable
      v-bind:title="$t('appScheduleAssignUserSelector.selectAssignees')"
      v-bind:ok-title="$t('appScheduleAssignUserSelector.action.apply')"
      v-bind:cancel-title="$t('appScheduleAssignUserSelector.action.close')"
      v-on:ok="$_onClickApply"
      v-on:hidden="$_resetModelValue"
    >
      <!-- modal body -->
      <item-selector
        multi
        v-bind:titles="titles"
        valueField="user_id"
        v-bind:data="filteredUsers"
        v-model="modelValue"
      >
        <template v-slot:header>
          <div class="selector-header">
            <input-keyword
              v-model="keyword"
              v-bind:placeholder="
                $t('appScheduleAssignUserSelector.placeholder.search')
              "
              v-on:search="$_setUsersForDisplay"
            />
          </div>
        </template>
        <template v-slot:default="{ item }">
          <td>
            <div class="item-cell-inner">
              <b-card-img
                v-bind:src="item.profileImagePath"
                class="profile-icon"
                crossorigin="use-credentials"
              />
              {{ item.user_name }}
            </div>
          </td>
        </template>
      </item-selector>
    </ifield-b-modal>
  </div>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import { FUNCTION_TYPE } from '@/constants.js'

export default {
  name: 'AppScheduleAssignUserSelector',
  props: {
    /**
     * エレメントIDのprefix
     */
    idPrefix: {
      type: String,
      default: '',
    },
    /**
     * 対象ユーザーデータ
     */
    users: {
      type: Array,
      default: () => [],
    },
    /**
     * 選択値
     */
    value: {
      type: Array,
      default: () => [],
    },
    /**
     * 私が担当リンクを表示する場合はtrue
     */
    showAssignMeLink: {
      type: Boolean,
      default: false,
    },
    /**
     * バリデーション状態
     */
    state: {
      type: Boolean,
      default: null,
    },
  },
  data: function () {
    return {
      /**
       * 検索キーワードでフィルタリングしたユーザーデータ
       * @type {Array.<Object>}
       */
      filteredUsers: [],
      /**
       * 検索キーワード
       * @type {String}
       */
      keyword: '',
      /**
       * ユーザーセレクトモーダルのタイトル
       * @type {Array.<Object>}
       */
      titles: [
        {
          key: 'name',
          label: this.$t('appScheduleAssignUserSelector.label.userName'),
        },
      ],
      /**
       * v-model用選択値
       * @type {Array.<Number>}
       */
      modelValue: [],
      /**
       * 結果表示用BFormSelectのv-model用の値(ダミー)
       * @type {Array}
       */
      dummyValue: [],
    }
  },
  computed: {
    /**
     * エレメントID
     * @type {Object}
     */
    elementId: function () {
      const baseId = {
        value: 'assign-user-value',
        button: 'assign-user-select-button',
        link: 'assign-me-link',
        modal: 'assign-user-select-modal',
      }
      return Object.fromEntries(
        Object.entries(baseId).map(([key, value]) => [
          key,
          this.idPrefix ? `${this.idPrefix}-${value}` : value,
        ])
      )
    },
    /**
     * ログインユーザーのID
     * @type {Number}
     */
    loginUserId: function () {
      return this.$store.state.login.userId
    },
    /**
     * 選択コンポーネント表示用ユーザーデータ
     * @type {Array.<Object>}
     */
    usersModified: function () {
      return this.users.map((user) => ({
        ...user,
        profileImagePath: this.$_getProfileImageUrl(user.user_id),
      }))
    },
    /**
     * カレント選択ユーザー
     * @type {Array.<Object>}
     */
    currentUsers: function () {
      return this.users.filter((user) => this.value.includes(user.user_id))
    },
  },
  watch: {
    value: {
      immediate: true,
      handler: function (value) {
        this.modelValue = value ? [...value] : []
      },
    },
    selectedItemIds: function () {
      this.selectedItems = this.selectedItemIds
    },
  },
  mounted: function () {
    this.$log.info('AppScheduleAssignUserSelector mounted.')
  },
  methods: {
    /**
     * フォームを表示する
     * @public
     */
    showModal: async function () {
      // 初期化
      this.keyword = ''
      this.filteredUsers = this.usersModified
      this.$refs['modal'].show()
    },

    /**
     * プロフィール画像のURLを取得
     * @param {Number} userId ユーザーID
     * @return {string} プロフィール画像のURL
     */
    $_getProfileImageUrl: function (userId) {
      // ダウンロードファイルAPI
      const url = RestApiClient.createApiUrl('download_file')
      url.searchParams.append('id', userId)
      url.searchParams.append('thumbnail', 1)
      url.searchParams.append('attach_file_type', FUNCTION_TYPE.USER)
      return url.toString()
    },

    /**
     * 私が担当リンククリック時処理
     */
    $_onClickAssignMe: function () {
      this.modelValue = [this.loginUserId]
      this.$_emitSelectEnd()
    },

    /**
     * 表示用ユーザー設定
     */
    $_setUsersForDisplay: function (keyword) {
      this.keyword = keyword
      if (!this.keyword) this.filteredUsers = this.usersModified
      this.filteredUsers = this.usersModified.filter((datum) =>
        datum.user_name.includes(this.keyword)
      )
    },

    /**
     * 選択項目適用時処理
     */
    $_onClickApply: function () {
      this.$_emitSelectEnd()
      this.$refs['modal'].hide()
    },

    /**
     * セレクターの値をvalueにリセットする
     */
    $_resetModelValue: function () {
      this.modelValue = this.value ? [...this.value] : []
    },

    /**
     * 選択終了イベント発行
     */
    $_emitSelectEnd: function () {
      /**
       * 担当者選択終了イベント
       * @param {Array.<Number>} userIds 担当者ユーザーID配列
       */
      this.$emit('assigned-users-selectend', this.modelValue)
    },
  },
}
</script>

<style lang="scss" scoped>
.select-container {
  width: max(calc(50% - 15px), 15.5rem);
}

.input-multi-select {
  overflow-x: hidden;
  height: calc(
    14px + 5 * 1.5em
  ) !important; // 上下borderとpadding + 5 * optionのmax-height
  @include mixin-scrollbar;
  &::-webkit-scrollbar-track {
    border-top-right-radius: $border-radius-small;
    border-bottom-right-radius: $border-radius-small;
  }
}

.user-action-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 16px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.assign-me-link {
  font-size: $font-size-middle;
  color: $text-color-link;
  text-decoration: underline;
  cursor: pointer;
}

::v-deep .schedule-assign-user-selector-content {
  height: 640px;
}

::v-deep .schedule-assign-user-selector-body {
  background-color: #ffffff;
}

.selector-header {
  background-color: #d9dcdf;
  padding: 10px;
}

.item-cell-inner {
  display: flex;
  align-items: center;
  font-weight: $font-weight-bold;
}

.profile-icon {
  border: 1px solid #eeeeee;
  border-radius: $border-radius-small;
  margin-right: 10px;
  width: 40px !important;
  height: 40px !important;
}
</style>
